<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import { debounce } from "lodash-es";
import {
  NButton,
  NConfigProvider,
  NEmpty,
  NInput,
  NLayout,
  NPagination,
} from "naive-ui";

import OptionsGroup from "@/components/OptionsGroup.vue";
import { getNaiveUILocale } from "@/utils/i18n";
import { useTheme } from "@/utils/theme";
import { isMobileSkin } from "@/utils/utils";

import Memory from "./Memory.vue";
import { filterRarity, rarityMap } from "./consts";
import { getMemoryData } from "./utils";

import type { CharMemory } from "./types";

const isMobile = isMobileSkin();
const i18nConfig = getNaiveUILocale();
const { theme, themeOverrides, isDark } = useTheme();
const states = ref<string[]>([]);
const isLoaded = ref(false);
const sorting = ref("lmmr");
const order = ref(-1);
const searchTerm = ref("");
const charMemoryData = ref<CharMemory[]>([]);

const compareDate = (mmrx: CharMemory, mmry: CharMemory) => {
  const [datex, datey] = [mmrx, mmry].map((mmr) => {
    return sorting.value === "opt"
      ? new Date(1)
      : mmr.memories.slice().sort((ma, mb) => {
          return sorting.value === "lmmr"
            ? mb.time.getTime() - ma.time.getTime()
            : ma.time.getTime() - mb.time.getTime();
        })[0].time;
  });
  const result =
    datex.getTime() === datey.getTime()
      ? Number(mmrx.charID) - Number(mmry.charID)
      : datex.getTime() - datey.getTime();
  return order.value ? result * order.value : -result;
};
const filteredMemory = ref<CharMemory[]>([]);

function _calcMemory() {
  if (states.value.length === 0 && searchTerm.value === "") {
    filteredMemory.value = charMemoryData.value.toSorted(compareDate);
  }

  const rarity = states.value;
  const keyword = searchTerm.value;
  filteredMemory.value = charMemoryData.value
    .filter((charm) => {
      if (rarity.length > 0 && !rarity.includes(rarityMap[charm.rarity]))
        return false;

      return (
        charm.char.includes(keyword) ||
        charm.memories.some(
          (memory) =>
            memory.name.includes(keyword) ||
            memory.info.some((info) => info.intro.includes(keyword)),
        )
      );
    })
    .sort(compareDate);
  pagination.page = 1;
}
const calcMemory = debounce(_calcMemory, 300);

const pagination = reactive({
  page: 1,
  pageSize: 10,
  pageSizes: [10, 25, 50, 100],
  pageSlot: isMobile ? 5 : 9,
});
function onUpdatePageSize(pageSize: number) {
  pagination.pageSize = pageSize;
  pagination.page = 1;
  calcMemory();
}
const shownMemory = computed(() => {
  return filteredMemory.value.slice(
    pagination.pageSize * (pagination.page - 1),
    pagination.pageSize * pagination.page,
  );
});

onMounted(async () => {
  charMemoryData.value = await getMemoryData();
  const latestDate = Math.max(
    ...charMemoryData.value.map((charm) => {
      return Math.max(...charm.memories.map((x) => x.time.getTime()));
    }),
  );
  for (const charm of charMemoryData.value) {
    for (const m of charm.memories) {
      if (m.time.getTime() === latestDate) m.isNew = true;
    }
  }

  isLoaded.value = true;

  _calcMemory();
});
const sortModes = [
  { type: "opt", label: "干员上线" },
  { type: "fmmr", label: "首个密录" },
  { type: "lmmr", label: "最新密录" },
];
const orderModes = [
  { value: 1, icon: "mdi-sort-calendar-descending" },
  { value: -1, icon: "mdi-sort-calendar-ascending" },
];
const sortMode = (mode: string) => {
  calcMemory();
  sorting.value = mode;
};
const orderMode = (mode: number) => {
  calcMemory();
  order.value = mode;
};
const pickSize = () => {
  return isMobile ? "small" : "medium";
};
</script>

<template>
  <NConfigProvider
    preflight-style-disabled
    inline-theme-disabled
    :theme="theme"
    :theme-overrides="themeOverrides"
    :locale="i18nConfig.locale"
    :date-locale="i18nConfig.dateLocale"
  >
    <NLayout
      :class="[
        'mx-auto max-w-3xl p-2 antialiased lg:max-w-[90rem] md:p-4',
        isDark && 'prts-widget-dark',
      ]"
    >
      <table class="w-full border-collapse text-left important-table">
        <tbody class="align-baseline">
          <tr>
            <OptionsGroup
              v-model="states"
              :title="filterRarity.title"
              :options="filterRarity.options"
              @update:model-value="calcMemory"
            />
          </tr>
          <tr>
            <td>
              <div class="flex flex-row items-center justify-center">
                <span class="basis-1/8">排序</span>
                <div
                  class="flex basis-7/8 flex-row items-center justify-between"
                >
                  <div class="flex flex-wrap justify-start">
                    <NButton
                      v-for="(item, index) in sortModes"
                      :key="index"
                      class="m-1"
                      :disabled="!isLoaded"
                      :type="sorting === item.type ? 'info' : 'default'"
                      @click="sortMode(item.type)"
                      >{{ item.label }}</NButton
                    >
                  </div>
                  <div class="flex flex-wrap justify-end">
                    <NButton
                      v-for="(item, index) in orderModes"
                      :key="index"
                      class="m-1"
                      :disabled="!isLoaded"
                      :type="order === item.value ? 'info' : 'default'"
                      @click="orderMode(item.value)"
                      ><span :class="['text-2xl mdi', item.icon]"></span>
                    </NButton>
                  </div>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="flex items-center">
                <NInput
                  v-model:value="searchTerm"
                  class="my-2"
                  type="text"
                  placeholder="搜索干员名称/密录名称/引言文字"
                  @update:value="calcMemory"
                >
                  <template #prefix>
                    <span class="mdi mdi-magnify text-lg" />
                  </template>
                </NInput>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <NPagination
        v-model:page="pagination.page"
        class="my-2 justify-center"
        :item-count="filteredMemory.length"
        :page-size="pagination.pageSize"
        :page-sizes="pagination.pageSizes"
        :page-slot="pagination.pageSlot"
        :size="pickSize()"
        show-size-picker
        @update:page-size="onUpdatePageSize"
      />
      <div class="w-full flex flex-col flex-wrap lg:flex-row">
        <Memory
          v-for="charm in shownMemory"
          :key="charm.char"
          :char-memory="charm"
        >
        </Memory>
      </div>
      <NEmpty v-if="shownMemory.length === 0" description="无结果">
        <template #icon>
          <span class="mdi mdi-book-search text-5xl" />
        </template>
      </NEmpty>
      <NPagination
        v-model:page="pagination.page"
        class="my-2 justify-center"
        :item-count="filteredMemory.length"
        :page-size="pagination.pageSize"
        :page-sizes="pagination.pageSizes"
        :page-slot="pagination.pageSlot"
        :size="pickSize()"
        show-size-picker
        @update:page-size="onUpdatePageSize"
      />
    </NLayout>
  </NConfigProvider>
</template>

<style scoped>
@import "@/styles/dark-mode.scss";
</style>
